// E1 Phase 13 — Workspace crypto orchestration seam.
//
// THE single place privileged Workspace crypto happens on the client, so every
// lifecycle UI component stays crypto-free. It reuses the personal-account
// primitives (`@/lib/crypto`) and the device keypair in the `lvdr-crypto`
// IndexedDB store (`deviceKeyStore`) — P13 invents NO new crypto.
//
// Operations (all client-side; the server only ever stores wrapped blobs):
//   • create   — generate a WS-DEK on-device, wrap it to the owner's devices
//   • grant    — re-wrap a (mounted) WS-DEK to a grantee's devices
//   • mount    — unwrap THIS device's wrapped WS-DEK row to plaintext
//   • escrow   — wrap a WS-DEK to an org escrow pubkey; recover via the org key
//
// THE PLAINTEXT DEK NEVER PERSISTS. After mount it is cached in an in-memory Map
// (consumed by grant/escrow/re-key) and cleared on unmount / crypto session-evict
// / logout (see `clearWorkspaceDEK` / `clearAllWorkspaceDEKs`). Mirrors how the
// personal DEK is handled — memory only, never localStorage/IndexedDB.
import {
  generateDeviceKey,
  generateKey,
  unwrapMasterKEK,
  wrapMasterKEK,
} from '@/lib/crypto';
import { getDeviceKey } from '@/features/encryption/deviceKeyStore';

// --- base64 <-> bytes (chunked to avoid call-stack overflow on large buffers) ---

export function toBase64(bytes: Uint8Array): string {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

export function fromBase64(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

// --- wire shapes (snake_case — match the Go handlers) ---

/** A device's public key, from GET /api/v1/crypto/devices or grantees/:uid/devices. */
export interface DevicePubKey {
  device_id: string;
  public_key: string; // base64 X25519 public key
}

/** A WS-DEK wrapped to one device — the POST shape for create / grant. */
export interface WrappedKeyRow {
  device_id: string;
  wrapped_ws_dek: string; // base64
}

/** One org-owner device's wrapped copy of the org escrow PRIVATE key (escrow init). */
export interface OrgEscrowDeviceMaterial {
  device_id: string;
  owner_user_id: string;
  wrapped_escrow_private_key: string; // base64
}

// --- in-memory plaintext-DEK cache (mount → grant/escrow/rekey) ---

const dekCache = new Map<string, Uint8Array>();

/** Cache a mounted workspace's plaintext DEK for subsequent privileged ops. */
export function cacheWorkspaceDEK(workspaceId: string, dek: Uint8Array): void {
  dekCache.set(workspaceId, dek);
}

/** The cached plaintext DEK for a mounted workspace, or undefined if not mounted. */
export function getCachedWorkspaceDEK(workspaceId: string): Uint8Array | undefined {
  return dekCache.get(workspaceId);
}

/** Drop one workspace's cached DEK (on unmount). Zeroes the buffer first. */
export function clearWorkspaceDEK(workspaceId: string): void {
  const dek = dekCache.get(workspaceId);
  if (dek) dek.fill(0);
  dekCache.delete(workspaceId);
}

/**
 * Drop ALL cached DEKs (crypto session-evict / screen-lock / logout). MUST be
 * wired into the crypto session-kill handler and hardLogout — otherwise a
 * backgrounded tab keeps plaintext DEKs alive after the server evicted the
 * session keyring. See CLAUDE.md persisted-store / DEK-eviction conventions.
 */
export function clearAllWorkspaceDEKs(): void {
  for (const dek of dekCache.values()) dek.fill(0);
  dekCache.clear();
}

// --- create / grant: generate + wrap to devices ---

/**
 * Generate a fresh WS-DEK on-device and wrap it to every supplied device public
 * key (workspace create — the §2.3 trust fix: the server never sees plaintext).
 * Returns the plaintext DEK (cache it on the optimistic mount) + the wrapped rows.
 */
export async function generateWrappedDEKForDevices(
  devices: DevicePubKey[],
): Promise<{ dek: Uint8Array; wrapped: WrappedKeyRow[] }> {
  if (devices.length === 0) throw new Error('no devices to wrap the workspace key to');
  const dek = await generateKey();
  const wrapped = await wrapDEKForDevices(dek, devices);
  return { dek, wrapped };
}

/** Wrap an already-held (mounted) WS-DEK to each device's public key (grant). */
export async function wrapDEKForDevices(
  dek: Uint8Array,
  devices: DevicePubKey[],
): Promise<WrappedKeyRow[]> {
  if (devices.length === 0) throw new Error('grantee has no active devices');
  return Promise.all(
    devices.map(async (d) => ({
      device_id: d.device_id,
      wrapped_ws_dek: toBase64(await wrapMasterKEK(dek, fromBase64(d.public_key))),
    })),
  );
}

// --- mount: unwrap THIS device's row ---

/**
 * Unwrap the caller's wrapped WS-DEK to plaintext using this browser's device
 * private key (the `lvdr-crypto` IDB record). `rows` is the caller's own
 * key-material (GET /:id/key-material); we pick the row matching this device.
 * Throws if the device key is missing (not enrolled) or no row matches it
 * (device enrolled after the workspace was created/granted — needs a re-grant).
 */
export async function unwrapMyDEK(rows: WrappedKeyRow[], userId: string): Promise<Uint8Array> {
  const deviceKey = await getDeviceKey(userId);
  if (!deviceKey) {
    throw new Error('no device key on this browser — enroll this device first');
  }
  const mine = rows.find((r) => r.device_id === deviceKey.deviceId);
  if (!mine) {
    throw new Error('this device has no key for the workspace — ask an owner to re-grant access');
  }
  return unwrapMasterKEK(fromBase64(mine.wrapped_ws_dek), deviceKey.privateKey);
}

// --- escrow: wrap to org key / recover via org key ---

/** Wrap a (mounted) WS-DEK to the org escrow public key (enable escrow). */
export async function wrapDEKForEscrow(
  dek: Uint8Array,
  orgEscrowPublicKeyB64: string,
): Promise<string> {
  return toBase64(await wrapMasterKEK(dek, fromBase64(orgEscrowPublicKeyB64)));
}

/**
 * Recover a workspace DEK via org escrow (org owner, member unavailable):
 * unwrap the escrow PRIVATE key with this device, then unwrap the WS-DEK with it.
 */
export async function recoverDEKViaEscrow(
  escrowWrappedWSDEKB64: string,
  wrappedEscrowPrivateKeyB64: string,
  userId: string,
): Promise<Uint8Array> {
  const deviceKey = await getDeviceKey(userId);
  if (!deviceKey) throw new Error('no device key on this browser to perform recovery');
  const escrowPriv = await unwrapMasterKEK(fromBase64(wrappedEscrowPrivateKeyB64), deviceKey.privateKey);
  return unwrapMasterKEK(fromBase64(escrowWrappedWSDEKB64), escrowPriv);
}

/**
 * Generate an org escrow keypair on-device and wrap its PRIVATE key to each
 * org-owner device (one-time org escrow bootstrap). The server stores only the
 * public key + the wrapped private blobs — never the plaintext escrow private key.
 */
export async function generateOrgEscrowKeypair(
  ownerDevices: DevicePubKey[],
  ownerUserId: string,
): Promise<{ publicKey: string; ownerMaterial: OrgEscrowDeviceMaterial[] }> {
  if (ownerDevices.length === 0) throw new Error('org owner has no devices to hold the escrow key');
  const kp = await generateDeviceKey(); // X25519 box keypair = the escrow keypair
  const ownerMaterial = await Promise.all(
    ownerDevices.map(async (d) => ({
      device_id: d.device_id,
      owner_user_id: ownerUserId,
      wrapped_escrow_private_key: toBase64(await wrapMasterKEK(kp.privateKey, fromBase64(d.public_key))),
    })),
  );
  return { publicKey: toBase64(kp.publicKey), ownerMaterial };
}
